import { sha256 } from 'js-sha256'
import { useRouter } from 'next/router'
import {
  type BaseSyntheticEvent,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useForm } from 'react-hook-form'

import { type SanitySignupFormBlock } from '@data/sanity/queries/types/blocks'
import { type EventPayload, AnalyticsEventName } from '@lib/analytics'
import { AnalyticsContext } from '@lib/analytics-context'
import { LanguageContext } from '@lib/language-context'
import { type ErrorMessages } from '@lib/request'
import { getLinkPageUrl } from '@lib/routes'
import { addEmailToNewsletterList } from '@lib/services'
import { StringsContext } from '@lib/strings-context'
import { signupUser } from '@lib/user/request'
import { type SignupFormValues } from '@lib/user/types'

import Alert from '@components/alert'
import Button from '@components/buttons/button'
import Checkbox from '@components/checkbox'
import ComplexPortableText from '@components/complex-portable-text'
import InputField from '@components/input-field/with-form-register'
import TelInputField from '@components/tel-input-field'

type SignupFormProps = Pick<
  SanitySignupFormBlock,
  'service' | 'klaviyoListID' | 'signupFormStrings'
> & {
  className?: string
}

const SignupForm = ({
  service,
  klaviyoListID,
  signupFormStrings,
  className,
}: SignupFormProps) => {
  const { triggerAnalyticsEvent } = useContext(AnalyticsContext)
  const { locale } = useContext(LanguageContext)
  const strings = useContext(StringsContext)

  const router = useRouter()

  const {
    handleSubmit,
    register,
    reset,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<SignupFormValues>()
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({})

  const password = watch('password')
  const subscribeToNewsletter = watch('subscribeToNewsletter')

  const firstNameRegister = register('firstName', {
    required: signupFormStrings.signupFirstNameMissing,
  })
  const lastNameRegister = register('lastName', {
    required: signupFormStrings.signupLastNameMissing,
  })
  const emailRegister = register('email', {
    required: signupFormStrings.signupEmailMissing,
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
      message: strings.emailInvalid,
    },
  })
  register('phone', {
    required: signupFormStrings.signupPhoneMissing,
  })
  const passwordRegister = register('password', {
    required: signupFormStrings.signupPasswordMissing,
  })
  const repeatPasswordRegister = register('repeatPassword', {
    required: signupFormStrings.signupRepeatPasswordMissing,
    validate: (value) => {
      if (password !== value) {
        return signupFormStrings.signupRepeatPasswordInvalid
      }
    },
  })
  const subscribeToNewsletterRegister = register('subscribeToNewsletter')

  const newsletterListId = useMemo(() => {
    if (service === 'klaviyo') {
      return klaviyoListID
    }

    return null
  }, [klaviyoListID, service])

  const handlePhoneChange = useCallback(
    (value: string) => {
      setValue('phone', value.replace(/\(|\)|-|\s/g, ''))
      trigger('phone')
    },
    [setValue, trigger],
  )

  const onSubmit = useCallback(
    async (signupFormValues: SignupFormValues, event?: BaseSyntheticEvent) => {
      event?.preventDefault()

      setIsLoading(true)
      setIsError(false)
      setErrorMessages({})

      try {
        if (subscribeToNewsletter && !!newsletterListId) {
          await addEmailToNewsletterList(
            locale,
            service,
            newsletterListId,
            signupFormValues.email,
            `${signupFormValues.firstName} ${signupFormValues.lastName}`,
          )
          triggerAnalyticsEvent(AnalyticsEventName.NewsletterSignUp)
        }

        // Create user
        const signupResponse = await signupUser(locale, signupFormValues)

        if ('fieldErrors' in signupResponse && signupResponse.fieldErrors) {
          setErrorMessages(signupResponse.fieldErrors)
          setIsLoading(false)
          return
        }

        // Trigger signup event
        const eventPayload: EventPayload = {
          user_data: {
            sha256_email_address: sha256(signupFormValues.email),
            phone_number: signupFormValues.phone,
            address: {
              first_name: signupFormValues.firstName,
              last_name: signupFormValues.lastName,
            },
          },
        }
        triggerAnalyticsEvent(AnalyticsEventName.Signup, eventPayload)
      } catch (error) {
        console.log(error)
        setIsLoading(false)
        setIsError(true)
        return
      }

      reset()
      setIsLoading(false)

      // Redirect to signup success page
      const url = getLinkPageUrl({
        pageType: 'signupSuccessPage',
      })
      router.push(url, url, {
        locale,
      })
    },
    [
      locale,
      newsletterListId,
      reset,
      router,
      service,
      subscribeToNewsletter,
      triggerAnalyticsEvent,
    ],
  )

  const isDisabled =
    !!errors.firstName ||
    !!errors.lastName ||
    !!errors.email ||
    !!errors.phone ||
    !!errors.password ||
    !!errors.repeatPassword

  return (
    <form className={className} onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-2 gap-8 mb-8">
        <InputField
          type="text"
          formRegister={firstNameRegister}
          label={signupFormStrings.signupFirstName}
          placeholder={signupFormStrings.signupFirstNamePlaceholder}
          errorMessage={errorMessages?.firstName ?? errors.firstName?.message}
        />
        <InputField
          type="text"
          formRegister={lastNameRegister}
          label={signupFormStrings.signupLastName}
          placeholder={signupFormStrings.signupLastNamePlaceholder}
          errorMessage={errorMessages?.lastName ?? errors.lastName?.message}
        />
      </div>

      <hr className="mt-0 mb-8 bg-divider" />

      <div className="grid grid-cols-2 gap-8 mb-8">
        <InputField
          type="email"
          autoComplete="email"
          formRegister={emailRegister}
          label={signupFormStrings.signupEmail}
          placeholder={signupFormStrings.signupEmailPlaceholder}
          errorMessage={errorMessages?.email ?? errors.email?.message}
        />
        <TelInputField
          name="phone"
          label={signupFormStrings.signupPhone}
          placeholder={signupFormStrings.signupPhonePlaceholder}
          errorMessage={errorMessages?.phone ?? errors.phone?.message}
          onChange={handlePhoneChange}
        />
      </div>

      <hr className="mt-0 mb-8 bg-divider" />

      <div className="grid grid-cols-2 gap-8 mb-8">
        <InputField
          type="password"
          autoComplete="off"
          formRegister={passwordRegister}
          label={signupFormStrings.signupPassword}
          placeholder={signupFormStrings.signupPasswordPlaceholder}
          errorMessage={errorMessages?.password ?? errors.password?.message}
        />
        <InputField
          type="password"
          autoComplete="off"
          formRegister={repeatPasswordRegister}
          label={signupFormStrings.signupRepeatPassword}
          placeholder={signupFormStrings.signupRepeatPasswordPlaceholder}
          errorMessage={
            errorMessages?.repeatPassword ?? errors.repeatPassword?.message
          }
        />
      </div>

      <Checkbox
        formRegister={subscribeToNewsletterRegister}
        className="mb-4 rc rc-checkbox"
      >
        <p className="m-0">{signupFormStrings.newsletterLabel}</p>
      </Checkbox>

      {(isError || errorMessages?.[0]) && (
        <div key="error" className="mt-8">
          <Alert error>
            <ComplexPortableText
              className="rc rc-alert rc-error"
              content={signupFormStrings.signupErrorMessage}
            />
          </Alert>
        </div>
      )}

      <div className="flex justify-center mt-8">
        <Button
          type="submit"
          variant="filled"
          disabled={isLoading || isDisabled}
          className="min-w-[260px]"
        >
          {isLoading
            ? strings.buttonSubmitting
            : signupFormStrings.signupSubmit}
        </Button>
      </div>
    </form>
  )
}

export default SignupForm
